import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip } from '../../../components';

export const LocalSection = ({ id, data }) => {
  const [localDirectoriesExpanded, setLocalDirectoriesExpanded] = useState(false);

  const defaultCriteria = { status: 'error', iconType: 'error', isEmpty: true, message: '', importance: { level: 0, text: '' }, solvability: { level: 0, text: '' } };
  const localDirectories = data?.localDirectories || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  return (
    <ResultsItem 
      id={id}
      title="Local" 
      tooltip="Your availability in local directories can make or break the attention you get from customers who are geographically close to your business. Be sure to create a free Google My Business page for your company so that you appear in searches for local keywords."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="Local Directories"
        tooltip="Your Google My Business listing information."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={localDirectories?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!localDirectories?.isEmpty && localDirectories?.data?.listings?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {localDirectories?.data?.listings?.map((listing, idx) => {
                  const directoryName = listing?.directoryName || '';
                  const name = listing?.name || '';
                  const title = directoryName && name ? `${directoryName} - ${name}` : (directoryName || name);
                  const titleHref = listing?.pageLink || '';

                  return (
                    <div
                      key={`${listing?.directoryName || 'directory'}-${idx}`}
                      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    >
                      {title ? (
                        <p className="text-primary">
                          {titleHref ? (
                            <a className="seo-checker-inline-link" href={titleHref} target="_blank" rel="noopener noreferrer">
                              {title}
                            </a>
                          ) : (
                            title
                          )}
                        </p>
                      ) : null}

                      {listing?.address ? <p className="text-secondary">{listing?.address}</p> : null}
                      {listing?.phone ? <p className="text-secondary">{listing?.phone}</p> : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-primary">{localDirectories?.message || 'No Local Directories were found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={localDirectoriesExpanded}
            onClick={() => setLocalDirectoriesExpanded(!localDirectoriesExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={localDirectoriesExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Social Profile Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Sign up for Google My Business page.</li>
                <li>Make sure that your name, address, and phone number are accurate and the same across all local listing sites.</li>
                <li>Add as many images as possible to give users a view of your location, your products, and your services.</li>
              </ul>
            </IconContent>
          }
          sideContent={
            <PriorityIndicatorGroup>
              <PriorityIndicator text="High Impact" level={3} />
              <PriorityIndicator text="Easy to Solve" level={1} />
            </PriorityIndicatorGroup>
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="https://www.webfx.com/blog/seo/seo-for-local-businesses/" target="_blank" rel="noopener noreferrer">
                    SEO for Local Businesses Guide: 9 Tips You Need to Try
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/seo/local-seo-checklist/" target="_blank" rel="noopener noreferrer">
                    Local SEO Checklist: How to Get Started with Local SEO
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/local-seo/learn/ranking-factors/" target="_blank" rel="noopener noreferrer">
                    4 Most Important Local SEO Ranking Factors
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
    </ResultsItem>
  );
};
